import { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Mail, Plus, Trash2, Send, Edit2, Save, X, Loader2, 
  FileText, History, Upload, Paperclip, Image as ImageIcon,
  Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, AlignRight, Link2, FolderOpen, Eye, Settings
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TiptapImage from '@tiptap/extension-image';
import TiptapLink from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import TiptapUnderline from '@tiptap/extension-underline';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import EmailDetailModal from '@/components/EmailDetailModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryOptions = [
  { value: 'confirmation', label: 'Booking Confirmation' },
  { value: 'follow_up', label: 'Follow Up' },
  { value: 'reminder', label: 'Reminder' },
  { value: 'information', label: 'Information' },
  { value: 'general', label: 'General' },
];

const defaultTemplates = [
  {
    name: 'Booking Confirmation',
    subject: 'Your Booking at Big Bear Estate is Confirmed!',
    category: 'confirmation',
    body: `<p>Dear {{first_name}},</p>
<p>Great news! Your booking at <strong>Big Bear Estate</strong> has been confirmed.</p>
<div style="background: #f5f5f5; padding: 15px; border-radius: 8px; margin: 15px 0;">
  <p><strong>Check-in:</strong> {{check_in_date}} (Friday, 4:30 PM)</p>
  <p><strong>Check-out:</strong> {{check_out_date}} (Friday, 11:00 AM)</p>
  <p><strong>Guests:</strong> {{num_occupants}}</p>
</div>
<p>We will send you detailed arrival instructions closer to your check-in date.</p>
<p>We look forward to hosting you!</p>`
  },
  {
    name: 'Follow Up - No Response',
    subject: 'Following Up on Your Big Bear Estate Inquiry',
    category: 'follow_up',
    body: `<p>Dear {{first_name}},</p>
<p>I wanted to follow up on your recent inquiry for Big Bear Estate.</p>
<p>We have availability for your requested dates and would love to help you finalize your booking.</p>
<p>Please let me know if you have any questions or if you'd like to proceed with the reservation.</p>`
  },
  {
    name: 'Payment Reminder',
    subject: 'Payment Reminder - Big Bear Estate',
    category: 'reminder',
    body: `<p>Dear {{first_name}},</p>
<p>This is a friendly reminder that payment for your upcoming stay at Big Bear Estate is due.</p>
<p>Please submit your payment at your earliest convenience to secure your reservation.</p>
<p>If you have any questions about the payment process, please don't hesitate to reach out.</p>`
  }
];

export default function EmailManager() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [templates, setTemplates] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [renters, setRenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('compose');
  const [filterCategory, setFilterCategory] = useState('all');
  
  // Template form state
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    subject: '',
    body: '',
    category: 'general'
  });
  
  // Compose email state
  const [composeForm, setComposeForm] = useState({
    recipient_email: '',
    recipient_name: '',
    cc_email: 'neill@neilldixon.com',
    subject: '',
    body: ''
  });
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedRecipient, setSelectedRecipient] = useState('');
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const quillRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  
  // Dialog state
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const templateImageInputRef = useRef(null);
  
  // Email Settings state
  const [emailSettings, setEmailSettings] = useState({
    sender_name: 'Kawartha Luxury Properties',
    sender_email: 'info@kawartha-luxury-properties.com',
    cc_email: 'neill@neilldixon.com',
    signature: ''
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const signatureImageInputRef = useRef(null);
  
  // Signature editor
  const signatureEditor = useEditor({
    extensions: [
      StarterKit,
      TiptapImage.configure({
        inline: true,
        allowBase64: true,
      }),
      TiptapLink.configure({
        openOnClick: false,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      TiptapUnderline,
    ],
    content: emailSettings.signature || '',
    onUpdate: ({ editor }) => {
      setEmailSettings(prev => ({ ...prev, signature: editor.getHTML() }));
    },
  });
  
  // Update signature editor when settings are fetched
  useEffect(() => {
    if (signatureEditor && emailSettings.signature && signatureEditor.getHTML() !== emailSettings.signature) {
      signatureEditor.commands.setContent(emailSettings.signature);
    }
  }, [emailSettings.signature, signatureEditor]);
  
  // Pre-filled data from URL params (for inquiry/renter emails)
  const [prefilledData, setPrefilledData] = useState(null);

  // File library state
  const [libraryFiles, setLibraryFiles] = useState([]);
  const [selectedLibraryFiles, setSelectedLibraryFiles] = useState([]);

  // Fetch file library
  useEffect(() => {
    const fetchLibraryFiles = async () => {
      try {
        const res = await axios.get(`${API}/file-library`);
        setLibraryFiles(res.data);
      } catch (error) {
        console.error('Error fetching file library:', error);
      }
    };
    fetchLibraryFiles();
  }, []);

  // Check for URL parameters on mount and pre-populate form
  useEffect(() => {
    const to = searchParams.get('to');
    const firstName = searchParams.get('firstName');
    const lastName = searchParams.get('lastName');
    const checkIn = searchParams.get('checkIn');
    const checkOut = searchParams.get('checkOut');
    const guests = searchParams.get('guests');
    const type = searchParams.get('type');
    const rentalAmount = searchParams.get('rentalAmount');
    const totalWithHst = searchParams.get('totalWithHst');
    const payment1Amount = searchParams.get('payment1Amount');
    const payment1Date = searchParams.get('payment1Date');
    const payment2Amount = searchParams.get('payment2Amount');
    const payment2Date = searchParams.get('payment2Date');
    const payment3Amount = searchParams.get('payment3Amount');
    const payment3Date = searchParams.get('payment3Date');
    
    if (to) {
      const fullName = [firstName, lastName].filter(Boolean).join(' ');
      setComposeForm(prev => ({
        ...prev,
        recipient_email: to,
        recipient_name: fullName,
      }));
      
      // Store prefilled data for placeholder replacement
      setPrefilledData({
        first_name: firstName || '',
        last_name: lastName || '',
        check_in_date: checkIn || '',
        check_out_date: checkOut || '',
        num_occupants: guests || '',
        num_guests: guests || '',
        rental_amount: rentalAmount || '',
        total_with_hst: totalWithHst || '',
        payment_1_amount: payment1Amount || '',
        payment_1_date: payment1Date || '',
        payment_2_amount: payment2Amount || '',
        payment_2_date: payment2Date || '',
        payment_3_amount: payment3Amount || '',
        payment_3_date: payment3Date || '',
        type: type || 'inquiry',
      });
      
      // Clear URL params after reading
      setSearchParams({});
      
      // Set active tab to compose
      setActiveTab('compose');
    }
  }, [searchParams, setSearchParams]);

  // Filtered templates based on category
  const filteredTemplates = filterCategory === 'all' 
    ? templates 
    : templates.filter(t => t.category === filterCategory);

  // TipTap Editor for Compose
  const editor = useEditor({
    extensions: [
      StarterKit,
      TiptapUnderline,
      TiptapImage.configure({
        inline: true,
        allowBase64: true,
      }),
      TiptapLink.configure({
        openOnClick: false,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: composeForm.body,
    onUpdate: ({ editor }) => {
      setComposeForm(prev => ({ ...prev, body: editor.getHTML() }));
    },
  });

  // TipTap Editor for Template Dialog
  const templateEditor = useEditor({
    extensions: [
      StarterKit,
      TiptapUnderline,
      TiptapImage.configure({
        inline: true,
        allowBase64: true,
      }),
      TiptapLink.configure({
        openOnClick: false,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: templateForm.body,
    onUpdate: ({ editor }) => {
      setTemplateForm(prev => ({ ...prev, body: editor.getHTML() }));
    },
  });

  // Update editor content when body changes externally (e.g., loading template)
  useEffect(() => {
    if (editor && composeForm.body !== editor.getHTML()) {
      editor.commands.setContent(composeForm.body || '');
    }
  }, [composeForm.body, editor]);

  // Update template editor content when templateForm.body changes
  useEffect(() => {
    if (templateEditor && templateForm.body !== templateEditor.getHTML()) {
      templateEditor.commands.setContent(templateForm.body || '');
    }
  }, [templateForm.body, templateEditor]);

  useEffect(() => {
    fetchData();
    fetchEmailSettings();
  }, []);

  const fetchData = async () => {
    try {
      const [templatesRes, logsRes, inquiriesRes, rentersRes] = await Promise.all([
        axios.get(`${API}/email-templates`),
        axios.get(`${API}/email-logs`),
        axios.get(`${API}/inquiries`),
        axios.get(`${API}/renters`)
      ]);
      // Sort templates alphabetically by name
      const sortedTemplates = templatesRes.data.sort((a, b) => 
        (a.name || '').localeCompare(b.name || '')
      );
      setTemplates(sortedTemplates);
      setEmailLogs(logsRes.data);
      setInquiries(inquiriesRes.data);
      setRenters(rentersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailSettings = async () => {
    try {
      const res = await axios.get(`${API}/email-settings`);
      setEmailSettings(res.data);
    } catch (error) {
      console.error('Error fetching email settings:', error);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/email-settings`, emailSettings);
      toast.success('Email settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setTemplateForm({ name: '', subject: '', body: '', category: 'general' });
    setTemplateDialogOpen(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      subject: template.subject,
      body: template.body,
      category: template.category
    });
    setTemplateDialogOpen(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name || !templateForm.subject || !templateForm.body) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setSaving(true);
    try {
      if (editingTemplate) {
        await axios.put(`${API}/email-templates/${editingTemplate.id}`, templateForm);
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/email-templates`, templateForm);
        toast.success('Template created');
      }
      setTemplateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async () => {
    if (!templateToDelete) return;
    
    try {
      await axios.delete(`${API}/email-templates/${templateToDelete.id}`);
      toast.success('Template deleted');
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const handleLoadDefaultTemplates = async () => {
    setSaving(true);
    try {
      for (const template of defaultTemplates) {
        await axios.post(`${API}/email-templates`, template);
      }
      toast.success('Default templates loaded');
      fetchData();
    } catch (error) {
      toast.error('Failed to load default templates');
    } finally {
      setSaving(false);
    }
  };

  const handleSelectTemplate = (templateId) => {
    setSelectedTemplate(templateId);
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setComposeForm(prev => ({
        ...prev,
        subject: template.subject,
        body: template.body
      }));
    }
  };

  const handleSelectRecipient = (recipientId) => {
    setSelectedRecipient(recipientId);
    
    // Check inquiries first
    const inquiry = inquiries.find(i => i.id === recipientId);
    if (inquiry) {
      setComposeForm(prev => ({
        ...prev,
        recipient_email: inquiry.email,
        recipient_name: `${inquiry.first_name} ${inquiry.last_name}`
      }));
      
      // Replace placeholders in subject and body
      if (composeForm.subject || composeForm.body) {
        setComposeForm(prev => ({
          ...prev,
          subject: replacePlaceholders(prev.subject, inquiry),
          body: replacePlaceholders(prev.body, inquiry)
        }));
      }
      return;
    }
    
    // Check renters
    const renter = renters.find(r => r.id === recipientId);
    if (renter) {
      setComposeForm(prev => ({
        ...prev,
        recipient_email: renter.email,
        recipient_name: `${renter.first_name} ${renter.last_name}`
      }));
      
      // Replace placeholders
      if (composeForm.subject || composeForm.body) {
        setComposeForm(prev => ({
          ...prev,
          subject: replacePlaceholders(prev.subject, renter),
          body: replacePlaceholders(prev.body, renter)
        }));
      }
    }
  };

  const replacePlaceholders = (text, data) => {
    if (!text || !data) return text;
    
    // Calculate remaining balance if not provided
    const totalOwing = data.total_with_hst || 0;
    const payment1 = data.payment_1_amount || 0;
    const payment2 = data.payment_2_amount || 0;
    const payment3 = data.payment_3_amount || 0;
    const remainingBalance = totalOwing - payment1 - payment2 - payment3;
    
    // Also handle hst field (might be stored as hst_percent)
    const hstValue = data.hst || data.hst_percent || '';
    
    return text
      // Guest Info
      .replace(/\{\{first_name\}\}/g, data.first_name || '')
      .replace(/\{\{last_name\}\}/g, data.last_name || '')
      .replace(/\{\{email\}\}/g, data.email || '')
      .replace(/\{\{num_occupants\}\}/g, data.num_occupants || data.num_guests || '')
      // Dates/Times
      .replace(/\{\{check_in_date\}\}/g, data.check_in_date || '')
      .replace(/\{\{check_in_time\}\}/g, data.check_in_time || '')
      .replace(/\{\{check_out_date\}\}/g, data.check_out_date || '')
      .replace(/\{\{check_out_time\}\}/g, data.check_out_time || '')
      // Pricing
      .replace(/\{\{rental_amount\}\}/g, data.rental_amount != null ? `$${Number(data.rental_amount).toFixed(2)}` : '')
      .replace(/\{\{hst\}\}/g, hstValue)
      .replace(/\{\{hst_type\}\}/g, data.hst_type || '')
      .replace(/\{\{total_with_hst\}\}/g, data.total_with_hst != null ? `$${Number(data.total_with_hst).toFixed(2)}` : '')
      .replace(/\{\{security_deposit\}\}/g, data.security_deposit != null ? `$${Number(data.security_deposit).toFixed(2)}` : '')
      // Payments
      .replace(/\{\{payment_1_amount\}\}/g, data.payment_1_amount != null ? `$${Number(data.payment_1_amount).toFixed(2)}` : '')
      .replace(/\{\{payment_1_date\}\}/g, data.payment_1_date || '')
      .replace(/\{\{payment_2_amount\}\}/g, data.payment_2_amount != null ? `$${Number(data.payment_2_amount).toFixed(2)}` : '')
      .replace(/\{\{payment_2_date\}\}/g, data.payment_2_date || '')
      .replace(/\{\{payment_3_amount\}\}/g, data.payment_3_amount != null ? `$${Number(data.payment_3_amount).toFixed(2)}` : '')
      .replace(/\{\{payment_3_date\}\}/g, data.payment_3_date || '')
      .replace(/\{\{remaining_balance\}\}/g, `$${remainingBalance.toFixed(2)}`);
  };

  // Handle image upload for inline images
  const handleImageUpload = async (file) => {
    if (!file) return null;
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('alt', file.name);
    formData.append('category', 'email');
    
    try {
      const res = await axios.post(`${API}/gallery/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
    } catch (error) {
      toast.error('Failed to upload image');
      return null;
    }
  };

  // Handle paste event for images
  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        e.preventDefault();
        const file = items[i].getAsFile();
        const imageUrl = await handleImageUpload(file);
        if (imageUrl && editor) {
          editor.chain().focus().setImage({ src: imageUrl }).run();
        }
        break;
      }
    }
  };

  // Handle attachment upload
  const handleAttachmentUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingAttachment(true);
    try {
      for (let file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        const res = await axios.post(`${API}/email-templates/upload-attachment`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        
        setAttachments(prev => [...prev, {
          filename: res.data.filename,
          stored_filename: res.data.stored_filename,
          url: res.data.url
        }]);
      }
      toast.success('Attachment(s) uploaded');
    } catch (error) {
      toast.error('Failed to upload attachment');
    } finally {
      setUploadingAttachment(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle image button click
  const handleImageButtonClick = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const imageUrl = await handleImageUpload(file);
    if (imageUrl && editor) {
      editor.chain().focus().setImage({ src: imageUrl }).run();
    }
    
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  // Add link
  const addLink = () => {
    const url = window.prompt('Enter URL:');
    if (url && editor) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  // Add link for template editor
  const addTemplateLink = () => {
    const url = window.prompt('Enter URL:');
    if (url && templateEditor) {
      templateEditor.chain().focus().setLink({ href: url }).run();
    }
  };

  // Handle template image button click
  const handleTemplateImageButtonClick = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const imageUrl = await handleImageUpload(file);
    if (imageUrl && templateEditor) {
      templateEditor.chain().focus().setImage({ src: imageUrl }).run();
    }
    
    if (templateImageInputRef.current) {
      templateImageInputRef.current.value = '';
    }
  };

  // Handle paste event for template editor images
  const handleTemplatePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        e.preventDefault();
        const file = items[i].getAsFile();
        const imageUrl = await handleImageUpload(file);
        if (imageUrl && templateEditor) {
          templateEditor.chain().focus().setImage({ src: imageUrl }).run();
        }
        break;
      }
    }
  };

  // Remove attachment
  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const getRecipientData = () => {
    // First, check if we have prefilled data from URL params
    if (prefilledData) {
      return prefilledData;
    }
    
    // Find the selected recipient's data
    if (selectedRecipient) {
      const inquiry = inquiries.find(i => i.id === selectedRecipient);
      if (inquiry) return inquiry;
      
      const renter = renters.find(r => r.id === selectedRecipient);
      if (renter) return renter;
    }
    
    // Try to find by email if no selection
    if (composeForm.recipient_email) {
      const inquiry = inquiries.find(i => i.email === composeForm.recipient_email);
      if (inquiry) return inquiry;
      
      const renter = renters.find(r => r.email === composeForm.recipient_email);
      if (renter) return renter;
    }
    
    return null;
  };

  const handleSendEmail = async () => {
    if (!composeForm.recipient_email || !composeForm.subject || !composeForm.body) {
      toast.error('Please fill in recipient, subject, and message');
      return;
    }
    
    setSending(true);
    try {
      // Get recipient data for placeholder replacement
      const recipientData = getRecipientData();
      
      // Replace placeholders in subject and body before sending
      const finalSubject = recipientData 
        ? replacePlaceholders(composeForm.subject, recipientData) 
        : composeForm.subject;
      const finalBody = recipientData 
        ? replacePlaceholders(composeForm.body, recipientData) 
        : composeForm.body;
      
      const emailData = {
        ...composeForm,
        subject: finalSubject,
        body: finalBody,
        // Pass attachment objects with both url and original filename
        attachments: attachments.map(a => ({
          url: a.url,
          filename: a.filename
        })),
        library_file_ids: selectedLibraryFiles
      };
      
      const response = await axios.post(`${API}/send-email`, emailData);
      const attachmentCount = response.data.attachments_sent || 0;
      toast.success(`Email sent successfully${attachmentCount > 0 ? ` with ${attachmentCount} attachment(s)` : ''}!`);
      setComposeForm({ recipient_email: '', recipient_name: '', cc_email: 'neill@neilldixon.com', subject: '', body: '' });
      setSelectedTemplate('');
      setSelectedRecipient('');
      setAttachments([]);
      setSelectedLibraryFiles([]);
      setPrefilledData(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#1B4D3E]" />
      </div>
    );
  }

  const allRecipients = [
    ...inquiries.map(i => ({ 
      id: i.id, 
      name: `${i.first_name} ${i.last_name}`, 
      email: i.email,
      type: 'inquiry',
      dates: `${i.check_in_date} - ${i.check_out_date}`
    })),
    ...renters.map(r => ({ 
      id: r.id, 
      name: `${r.first_name} ${r.last_name}`, 
      email: r.email,
      type: 'renter',
      dates: `${r.check_in_date} - ${r.check_out_date}`
    }))
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-serif text-3xl text-[#1C1C1C] mb-2">Email Client</h1>
          <p className="text-[#66605B]">Send emails and manage templates</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="compose" className="flex items-center gap-2">
            <Send className="w-4 h-4" />
            Compose
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            Sent History
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="w-4 h-4" />
            Settings
          </TabsTrigger>
        </TabsList>

        {/* Compose Tab */}
        <TabsContent value="compose">
          <div className="bg-white rounded-sm shadow-sm p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Template Selection */}
              <div className="space-y-2">
                <Label>Load Template</Label>
                <Select value={selectedTemplate} onValueChange={handleSelectTemplate}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a template..." />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Recipient Selection */}
              <div className="space-y-2">
                <Label>Select Recipient</Label>
                <Select value={selectedRecipient} onValueChange={handleSelectRecipient}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select recipient..." />
                  </SelectTrigger>
                  <SelectContent>
                    {allRecipients.length === 0 ? (
                      <SelectItem value="none" disabled>No recipients available</SelectItem>
                    ) : (
                      allRecipients.map(r => (
                        <SelectItem key={r.id} value={r.id}>
                          <span className="flex items-center gap-2">
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              r.type === 'renter' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                            }`}>
                              {r.type === 'renter' ? 'Renter' : 'Inquiry'}
                            </span>
                            {r.name} - {r.email}
                          </span>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Prefilled Data Banner */}
            {prefilledData && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-blue-800">
                      Composing email for {prefilledData.type === 'inquiry' ? 'Inquiry' : 'Renter'}
                    </p>
                    <div className="text-sm text-blue-700 mt-1">
                      <p><strong>{prefilledData.first_name} {prefilledData.last_name}</strong></p>
                      {prefilledData.check_in_date && (
                        <p>Dates: {prefilledData.check_in_date} → {prefilledData.check_out_date}</p>
                      )}
                      {(prefilledData.num_occupants || prefilledData.num_guests) && (
                        <p>Guests: {prefilledData.num_occupants || prefilledData.num_guests}</p>
                      )}
                      {prefilledData.total_with_hst && (
                        <p>Total: ${parseFloat(prefilledData.total_with_hst).toLocaleString()}</p>
                      )}
                    </div>
                    <p className="text-xs text-blue-600 mt-2">
                      Select a template below and placeholders will be auto-filled with this data.
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPrefilledData(null)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="recipient_email">To (Email) *</Label>
                <Input
                  id="recipient_email"
                  type="email"
                  value={composeForm.recipient_email}
                  onChange={(e) => setComposeForm(prev => ({ ...prev, recipient_email: e.target.value }))}
                  placeholder="recipient@email.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="recipient_name">Recipient Name</Label>
                <Input
                  id="recipient_name"
                  value={composeForm.recipient_name}
                  onChange={(e) => setComposeForm(prev => ({ ...prev, recipient_name: e.target.value }))}
                  placeholder="John Doe"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cc_email">CC</Label>
              <Input
                id="cc_email"
                type="email"
                value={composeForm.cc_email}
                onChange={(e) => setComposeForm(prev => ({ ...prev, cc_email: e.target.value }))}
                placeholder="cc@email.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="subject">Subject *</Label>
              <Input
                id="subject"
                value={composeForm.subject}
                onChange={(e) => setComposeForm(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Email subject"
              />
            </div>

            <div className="space-y-2">
              <Label>Message *</Label>
              
              {/* Toolbar */}
              <div className="flex flex-wrap items-center gap-1 p-2 bg-[#F9F8F6] border border-[#E5E0DB] rounded-t-md">
                <button
                  type="button"
                  onClick={() => editor?.chain().focus().toggleBold().run()}
                  className={`p-2 rounded hover:bg-[#E5E0DB] ${editor?.isActive('bold') ? 'bg-[#E5E0DB]' : ''}`}
                  title="Bold"
                >
                  <Bold className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => editor?.chain().focus().toggleItalic().run()}
                  className={`p-2 rounded hover:bg-[#E5E0DB] ${editor?.isActive('italic') ? 'bg-[#E5E0DB]' : ''}`}
                  title="Italic"
                >
                  <Italic className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => editor?.chain().focus().toggleUnderline().run()}
                  className={`p-2 rounded hover:bg-[#E5E0DB] ${editor?.isActive('underline') ? 'bg-[#E5E0DB]' : ''}`}
                  title="Underline"
                >
                  <Underline className="w-4 h-4" />
                </button>
                
                <div className="w-px h-6 bg-[#E5E0DB] mx-1" />
                
                <button
                  type="button"
                  onClick={() => editor?.chain().focus().toggleBulletList().run()}
                  className={`p-2 rounded hover:bg-[#E5E0DB] ${editor?.isActive('bulletList') ? 'bg-[#E5E0DB]' : ''}`}
                  title="Bullet List"
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                  className={`p-2 rounded hover:bg-[#E5E0DB] ${editor?.isActive('orderedList') ? 'bg-[#E5E0DB]' : ''}`}
                  title="Numbered List"
                >
                  <ListOrdered className="w-4 h-4" />
                </button>
                
                <div className="w-px h-6 bg-[#E5E0DB] mx-1" />
                
                <button
                  type="button"
                  onClick={() => editor?.chain().focus().setTextAlign('left').run()}
                  className={`p-2 rounded hover:bg-[#E5E0DB] ${editor?.isActive({ textAlign: 'left' }) ? 'bg-[#E5E0DB]' : ''}`}
                  title="Align Left"
                >
                  <AlignLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => editor?.chain().focus().setTextAlign('center').run()}
                  className={`p-2 rounded hover:bg-[#E5E0DB] ${editor?.isActive({ textAlign: 'center' }) ? 'bg-[#E5E0DB]' : ''}`}
                  title="Align Center"
                >
                  <AlignCenter className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => editor?.chain().focus().setTextAlign('right').run()}
                  className={`p-2 rounded hover:bg-[#E5E0DB] ${editor?.isActive({ textAlign: 'right' }) ? 'bg-[#E5E0DB]' : ''}`}
                  title="Align Right"
                >
                  <AlignRight className="w-4 h-4" />
                </button>
                
                <div className="w-px h-6 bg-[#E5E0DB] mx-1" />
                
                <button
                  type="button"
                  onClick={addLink}
                  className={`p-2 rounded hover:bg-[#E5E0DB] ${editor?.isActive('link') ? 'bg-[#E5E0DB]' : ''}`}
                  title="Add Link"
                >
                  <Link2 className="w-4 h-4" />
                </button>
                
                <input
                  type="file"
                  ref={imageInputRef}
                  accept="image/*"
                  onChange={handleImageButtonClick}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => imageInputRef.current?.click()}
                  className="p-2 rounded hover:bg-[#E5E0DB]"
                  title="Insert Image"
                >
                  <ImageIcon className="w-4 h-4" />
                </button>
              </div>
              
              {/* Editor */}
              <div 
                onPaste={handlePaste} 
                className="border border-t-0 border-[#E5E0DB] rounded-b-md min-h-[250px] prose prose-sm max-w-none"
              >
                <EditorContent 
                  editor={editor} 
                  className="p-4 min-h-[250px] focus:outline-none [&_.ProseMirror]:min-h-[220px] [&_.ProseMirror]:outline-none [&_.ProseMirror_img]:max-w-full [&_.ProseMirror_img]:h-auto [&_.ProseMirror_img]:rounded"
                />
              </div>
              
              <div className="space-y-2 mt-2">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-[#66605B] font-medium">Guest Info:</span>
                  {['{{first_name}}', '{{last_name}}', '{{email}}', '{{num_occupants}}'].map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => {
                        if (editor) {
                          editor.chain().focus().insertContent(p).run();
                        }
                      }}
                      className="px-2 py-1 bg-[#F0EBE5] text-[#1B4D3E] rounded hover:bg-[#E5E0DB] transition-colors font-mono"
                    >
                      {p}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-[#66605B] font-medium">Dates/Times:</span>
                  {['{{check_in_date}}', '{{check_in_time}}', '{{check_out_date}}', '{{check_out_time}}'].map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => {
                        if (editor) {
                          editor.chain().focus().insertContent(p).run();
                        }
                      }}
                      className="px-2 py-1 bg-[#F0EBE5] text-[#1B4D3E] rounded hover:bg-[#E5E0DB] transition-colors font-mono"
                    >
                      {p}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-[#66605B] font-medium">Pricing:</span>
                  {['{{rental_amount}}', '{{hst}}', '{{hst_type}}', '{{total_with_hst}}', '{{security_deposit}}'].map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => {
                        if (editor) {
                          editor.chain().focus().insertContent(p).run();
                        }
                      }}
                      className="px-2 py-1 bg-[#F0EBE5] text-[#1B4D3E] rounded hover:bg-[#E5E0DB] transition-colors font-mono"
                    >
                      {p}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-[#66605B] font-medium">Payments:</span>
                  {['{{payment_1_amount}}', '{{payment_1_date}}', '{{payment_2_amount}}', '{{payment_2_date}}', '{{payment_3_amount}}', '{{payment_3_date}}', '{{remaining_balance}}'].map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => {
                        if (editor) {
                          editor.chain().focus().insertContent(p).run();
                        }
                      }}
                      className="px-2 py-1 bg-[#F0EBE5] text-[#1B4D3E] rounded hover:bg-[#E5E0DB] transition-colors font-mono"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-xs text-[#66605B] mt-2">Tip: Paste images directly with Ctrl+V</p>
            </div>

            {/* Attachments Section */}
            <div className="space-y-3 pt-4 border-t">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Paperclip className="w-4 h-4" />
                  Attachments
                </Label>
                <div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleAttachmentUpload}
                    multiple
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingAttachment}
                  >
                    {uploadingAttachment ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Add Attachment
                  </Button>
                </div>
              </div>
              
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {attachments.map((att, index) => (
                    <div 
                      key={index}
                      className="flex items-center gap-2 px-3 py-2 bg-[#F0EBE5] rounded-lg text-sm"
                    >
                      <Paperclip className="w-4 h-4 text-[#1B4D3E]" />
                      <span className="max-w-[200px] truncate">{att.filename}</span>
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* File Library Selection */}
              {libraryFiles.length > 0 && (
                <div className="mt-4 p-4 bg-[#F5F3F0] rounded-lg">
                  <Label className="flex items-center gap-2 mb-3">
                    <FolderOpen className="w-4 h-4" />
                    Attach from File Library
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[200px] overflow-y-auto">
                    {libraryFiles.map((file) => (
                      <label 
                        key={file.id}
                        className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-colors ${
                          selectedLibraryFiles.includes(file.id) 
                            ? 'bg-[#1B4D3E] text-white' 
                            : 'bg-white hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedLibraryFiles.includes(file.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedLibraryFiles(prev => [...prev, file.id]);
                            } else {
                              setSelectedLibraryFiles(prev => prev.filter(id => id !== file.id));
                            }
                          }}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${
                            selectedLibraryFiles.includes(file.id) ? 'text-white' : 'text-[#1C1C1C]'
                          }`}>
                            {file.name}
                          </p>
                          <p className={`text-xs truncate ${
                            selectedLibraryFiles.includes(file.id) ? 'text-white/70' : 'text-[#66605B]'
                          }`}>
                            {file.original_filename}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                  {selectedLibraryFiles.length > 0 && (
                    <p className="text-xs text-[#1B4D3E] mt-2">
                      {selectedLibraryFiles.length} file(s) selected from library
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pt-4 border-t">
              <div className="text-sm text-[#66605B]">
                <p>Your signature will be automatically added.</p>
                <p className="text-xs mt-1">Tip: Paste images directly with Ctrl+V</p>
                {selectedRecipient && (
                  <p className="text-[#1B4D3E] font-medium mt-1">
                    ✓ Placeholders will be replaced with recipient's info when sent
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                {selectedRecipient && (composeForm.subject?.includes('{{') || composeForm.body?.includes('{{')) && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const recipientData = getRecipientData();
                      if (recipientData) {
                        setComposeForm(prev => ({
                          ...prev,
                          subject: replacePlaceholders(prev.subject, recipientData),
                          body: replacePlaceholders(prev.body, recipientData)
                        }));
                        toast.success('Placeholders replaced!');
                      } else {
                        toast.error('Select a recipient first');
                      }
                    }}
                  >
                    Preview with Data
                  </Button>
                )}
                <Button
                  onClick={handleSendEmail}
                  disabled={sending}
                  className="bg-[#1B4D3E] hover:bg-[#153E32]"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  Send Email
                </Button>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates">
          <div className="bg-white rounded-sm shadow-sm p-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <h2 className="font-serif text-xl text-[#1C1C1C]">Email Templates</h2>
              <div className="flex flex-wrap gap-2">
                {templates.length === 0 && (
                  <Button
                    variant="outline"
                    onClick={handleLoadDefaultTemplates}
                    disabled={saving}
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Load Defaults
                  </Button>
                )}
                <Button onClick={handleCreateTemplate} className="bg-[#1B4D3E] hover:bg-[#153E32]">
                  <Plus className="w-4 h-4 mr-2" />
                  New Template
                </Button>
              </div>
            </div>

            {/* Category Filter */}
            {templates.length > 0 && (
              <div className="mb-6 flex flex-wrap items-center gap-2">
                <span className="text-sm text-[#66605B] mr-2">Filter by:</span>
                <button
                  onClick={() => setFilterCategory('all')}
                  className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                    filterCategory === 'all'
                      ? 'bg-[#1B4D3E] text-white'
                      : 'bg-[#F0EBE5] text-[#66605B] hover:bg-[#E5E0DB]'
                  }`}
                >
                  All ({templates.length})
                </button>
                {categoryOptions.map(cat => {
                  const count = templates.filter(t => t.category === cat.value).length;
                  if (count === 0) return null;
                  return (
                    <button
                      key={cat.value}
                      onClick={() => setFilterCategory(cat.value)}
                      className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                        filterCategory === cat.value
                          ? 'bg-[#1B4D3E] text-white'
                          : 'bg-[#F0EBE5] text-[#66605B] hover:bg-[#E5E0DB]'
                      }`}
                    >
                      {cat.label} ({count})
                    </button>
                  );
                })}
              </div>
            )}

            {templates.length === 0 ? (
              <div className="text-center py-12 text-[#66605B]">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No templates yet. Create one or load the default templates.</p>
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className="text-center py-12 text-[#66605B]">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No templates in this category.</p>
                <button 
                  onClick={() => setFilterCategory('all')}
                  className="mt-2 text-[#1B4D3E] hover:underline"
                >
                  Show all templates
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredTemplates.map(template => (
                  <div key={template.id} className="border rounded-lg p-4 hover:shadow-sm transition-shadow">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-[#1C1C1C]">{template.name}</h3>
                          <span className="text-xs px-2 py-0.5 rounded bg-[#F0EBE5] text-[#66605B]">
                            {categoryOptions.find(c => c.value === template.category)?.label || template.category}
                          </span>
                        </div>
                        <p className="text-sm text-[#66605B]">Subject: {template.subject}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditTemplate(template)}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setTemplateToDelete(template);
                            setDeleteDialogOpen(true);
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <div className="bg-white rounded-sm shadow-sm p-6">
            <h2 className="font-serif text-xl text-[#1C1C1C] mb-6">Sent Emails</h2>
            
            {emailLogs.length === 0 ? (
              <div className="text-center py-12 text-[#66605B]">
                <Mail className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No emails sent yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {emailLogs.map(log => (
                  <div 
                    key={log.id} 
                    className="border rounded-lg p-4 cursor-pointer hover:bg-[#F9F8F6] hover:border-[#1B4D3E] transition-colors"
                    onClick={() => setSelectedEmail(log)}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[#1C1C1C]">
                          To: {log.recipient_name ? `${log.recipient_name} <${log.recipient_email}>` : log.recipient_email}
                        </p>
                        <p className="text-sm text-[#66605B] truncate">{log.subject}</p>
                        {(log.attachments_count > 0 || log.attachments?.length > 0) && (
                          <p className="text-xs text-[#1B4D3E] flex items-center gap-1 mt-1">
                            <Paperclip className="w-3 h-3" />
                            {log.attachments?.length || log.attachments_count} attachment(s)
                          </p>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0 ml-4">
                        <span className={`text-xs px-2 py-1 rounded ${
                          log.status === 'sent' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {log.status}
                        </span>
                        <p className="text-xs text-[#66605B] mt-1">
                          {new Date(log.sent_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[#1B4D3E] hover:text-[#153E32]"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedEmail(log);
                        }}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View Details
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <div className="bg-white rounded-sm shadow-sm p-6 space-y-6">
            <h2 className="font-serif text-xl text-[#1C1C1C] mb-6">Email Settings</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="sender_name">Sender Name</Label>
                <Input
                  id="sender_name"
                  value={emailSettings.sender_name || ''}
                  onChange={(e) => setEmailSettings({...emailSettings, sender_name: e.target.value})}
                  placeholder="e.g., Big Bear Cottage"
                />
                <p className="text-xs text-gray-500">The name that appears in the "From" field</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="sender_email">Sender Email</Label>
                <Input
                  id="sender_email"
                  type="email"
                  value={emailSettings.sender_email || ''}
                  onChange={(e) => setEmailSettings({...emailSettings, sender_email: e.target.value})}
                  placeholder="e.g., info@kawartha-luxury-properties.com"
                />
                <p className="text-xs text-gray-500">The email address that sends emails</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="cc_email">Default CC Email</Label>
                <Input
                  id="cc_email"
                  type="email"
                  value={emailSettings.cc_email || ''}
                  onChange={(e) => setEmailSettings({...emailSettings, cc_email: e.target.value})}
                  placeholder="e.g., neill@neilldixon.com"
                />
                <p className="text-xs text-gray-500">CC'd on all outgoing emails</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Email Signature</Label>
              <p className="text-xs text-gray-500 mb-2">Rich text signature appended to all outgoing emails. You can paste images directly.</p>
              
              {/* Signature Editor Toolbar */}
              {signatureEditor && (
                <div className="border rounded-t-lg p-2 bg-gray-50 flex flex-wrap gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => signatureEditor.chain().focus().toggleBold().run()}
                    className={signatureEditor.isActive('bold') ? 'bg-gray-200' : ''}
                  >
                    <Bold className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => signatureEditor.chain().focus().toggleItalic().run()}
                    className={signatureEditor.isActive('italic') ? 'bg-gray-200' : ''}
                  >
                    <Italic className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => signatureEditor.chain().focus().toggleUnderline().run()}
                    className={signatureEditor.isActive('underline') ? 'bg-gray-200' : ''}
                  >
                    <Underline className="h-4 w-4" />
                  </Button>
                  <div className="w-px h-6 bg-gray-300 mx-1" />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => signatureEditor.chain().focus().setTextAlign('left').run()}
                    className={signatureEditor.isActive({ textAlign: 'left' }) ? 'bg-gray-200' : ''}
                  >
                    <AlignLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => signatureEditor.chain().focus().setTextAlign('center').run()}
                    className={signatureEditor.isActive({ textAlign: 'center' }) ? 'bg-gray-200' : ''}
                  >
                    <AlignCenter className="h-4 w-4" />
                  </Button>
                  <div className="w-px h-6 bg-gray-300 mx-1" />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const url = window.prompt('Enter link URL:');
                      if (url) {
                        signatureEditor.chain().focus().setLink({ href: url }).run();
                      }
                    }}
                    className={signatureEditor.isActive('link') ? 'bg-gray-200' : ''}
                  >
                    <Link2 className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => signatureImageInputRef.current?.click()}
                  >
                    <ImageIcon className="h-4 w-4" />
                  </Button>
                  <input
                    type="file"
                    ref={signatureImageInputRef}
                    accept="image/*"
                    className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          const base64 = event.target?.result;
                          if (base64 && signatureEditor) {
                            signatureEditor.chain().focus().setImage({ src: base64 }).run();
                          }
                        };
                        reader.readAsDataURL(file);
                      }
                      e.target.value = '';
                    }}
                  />
                </div>
              )}
              
              {/* Signature Editor Content */}
              <div className="border border-t-0 rounded-b-lg min-h-[150px] p-3 bg-white prose prose-sm max-w-none">
                <EditorContent editor={signatureEditor} />
              </div>
            </div>
            
            <div className="flex justify-end pt-4 border-t">
              <Button 
                onClick={handleSaveSettings}
                disabled={savingSettings}
                className="bg-[#1B4D3E] hover:bg-[#153E32]"
              >
                {savingSettings ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Settings
                  </>
                )}
              </Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Email Detail Modal */}
      <EmailDetailModal 
        email={selectedEmail} 
        open={!!selectedEmail} 
        onClose={() => setSelectedEmail(null)} 
      />

      {/* Template Edit Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingTemplate ? 'Edit Template' : 'Create Template'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="template_name">Template Name *</Label>
                <Input
                  id="template_name"
                  value={templateForm.name}
                  onChange={(e) => setTemplateForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Booking Confirmation"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="template_category">Category</Label>
                <Select 
                  value={templateForm.category} 
                  onValueChange={(v) => setTemplateForm(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categoryOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="template_subject">Subject *</Label>
              <Input
                id="template_subject"
                value={templateForm.subject}
                onChange={(e) => setTemplateForm(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Email subject line"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Body *</Label>
              
              {/* Template Editor Toolbar */}
              <div className="flex flex-wrap items-center gap-1 p-2 bg-[#F9F8F6] border border-[#E5E0DB] rounded-t-md">
                <button
                  type="button"
                  onClick={() => templateEditor?.chain().focus().toggleBold().run()}
                  className={`p-2 rounded hover:bg-[#E5E0DB] ${templateEditor?.isActive('bold') ? 'bg-[#E5E0DB]' : ''}`}
                  title="Bold"
                >
                  <Bold className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => templateEditor?.chain().focus().toggleItalic().run()}
                  className={`p-2 rounded hover:bg-[#E5E0DB] ${templateEditor?.isActive('italic') ? 'bg-[#E5E0DB]' : ''}`}
                  title="Italic"
                >
                  <Italic className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => templateEditor?.chain().focus().toggleUnderline().run()}
                  className={`p-2 rounded hover:bg-[#E5E0DB] ${templateEditor?.isActive('underline') ? 'bg-[#E5E0DB]' : ''}`}
                  title="Underline"
                >
                  <Underline className="w-4 h-4" />
                </button>
                
                <div className="w-px h-6 bg-[#E5E0DB] mx-1" />
                
                <button
                  type="button"
                  onClick={() => templateEditor?.chain().focus().toggleBulletList().run()}
                  className={`p-2 rounded hover:bg-[#E5E0DB] ${templateEditor?.isActive('bulletList') ? 'bg-[#E5E0DB]' : ''}`}
                  title="Bullet List"
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => templateEditor?.chain().focus().toggleOrderedList().run()}
                  className={`p-2 rounded hover:bg-[#E5E0DB] ${templateEditor?.isActive('orderedList') ? 'bg-[#E5E0DB]' : ''}`}
                  title="Numbered List"
                >
                  <ListOrdered className="w-4 h-4" />
                </button>
                
                <div className="w-px h-6 bg-[#E5E0DB] mx-1" />
                
                <button
                  type="button"
                  onClick={() => templateEditor?.chain().focus().setTextAlign('left').run()}
                  className={`p-2 rounded hover:bg-[#E5E0DB] ${templateEditor?.isActive({ textAlign: 'left' }) ? 'bg-[#E5E0DB]' : ''}`}
                  title="Align Left"
                >
                  <AlignLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => templateEditor?.chain().focus().setTextAlign('center').run()}
                  className={`p-2 rounded hover:bg-[#E5E0DB] ${templateEditor?.isActive({ textAlign: 'center' }) ? 'bg-[#E5E0DB]' : ''}`}
                  title="Align Center"
                >
                  <AlignCenter className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => templateEditor?.chain().focus().setTextAlign('right').run()}
                  className={`p-2 rounded hover:bg-[#E5E0DB] ${templateEditor?.isActive({ textAlign: 'right' }) ? 'bg-[#E5E0DB]' : ''}`}
                  title="Align Right"
                >
                  <AlignRight className="w-4 h-4" />
                </button>
                
                <div className="w-px h-6 bg-[#E5E0DB] mx-1" />
                
                <button
                  type="button"
                  onClick={addTemplateLink}
                  className={`p-2 rounded hover:bg-[#E5E0DB] ${templateEditor?.isActive('link') ? 'bg-[#E5E0DB]' : ''}`}
                  title="Add Link"
                >
                  <Link2 className="w-4 h-4" />
                </button>
                
                <input
                  type="file"
                  ref={templateImageInputRef}
                  accept="image/*"
                  onChange={handleTemplateImageButtonClick}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => templateImageInputRef.current?.click()}
                  className="p-2 rounded hover:bg-[#E5E0DB]"
                  title="Insert Image"
                >
                  <ImageIcon className="w-4 h-4" />
                </button>
              </div>
              
              {/* Template Editor */}
              <div 
                onPaste={handleTemplatePaste} 
                className="border border-t-0 border-[#E5E0DB] rounded-b-md min-h-[200px] prose prose-sm max-w-none"
              >
                <EditorContent 
                  editor={templateEditor} 
                  className="p-4 min-h-[200px] focus:outline-none [&_.ProseMirror]:min-h-[170px] [&_.ProseMirror]:outline-none [&_.ProseMirror_img]:max-w-full [&_.ProseMirror_img]:h-auto [&_.ProseMirror_img]:rounded"
                />
              </div>
              
              <div className="space-y-2 mt-2">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-[#66605B] font-medium">Guest Info:</span>
                  {['{{first_name}}', '{{last_name}}', '{{email}}', '{{num_occupants}}'].map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => {
                        if (templateEditor) {
                          templateEditor.chain().focus().insertContent(p).run();
                        }
                      }}
                      className="px-2 py-1 bg-[#F0EBE5] text-[#1B4D3E] rounded hover:bg-[#E5E0DB] transition-colors font-mono"
                    >
                      {p}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-[#66605B] font-medium">Dates/Times:</span>
                  {['{{check_in_date}}', '{{check_in_time}}', '{{check_out_date}}', '{{check_out_time}}'].map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => {
                        if (templateEditor) {
                          templateEditor.chain().focus().insertContent(p).run();
                        }
                      }}
                      className="px-2 py-1 bg-[#F0EBE5] text-[#1B4D3E] rounded hover:bg-[#E5E0DB] transition-colors font-mono"
                    >
                      {p}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-[#66605B] font-medium">Pricing:</span>
                  {['{{rental_amount}}', '{{hst}}', '{{hst_type}}', '{{total_with_hst}}', '{{security_deposit}}'].map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => {
                        if (templateEditor) {
                          templateEditor.chain().focus().insertContent(p).run();
                        }
                      }}
                      className="px-2 py-1 bg-[#F0EBE5] text-[#1B4D3E] rounded hover:bg-[#E5E0DB] transition-colors font-mono"
                    >
                      {p}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-[#66605B] font-medium">Payments:</span>
                  {['{{payment_1_amount}}', '{{payment_1_date}}', '{{payment_2_amount}}', '{{payment_2_date}}', '{{payment_3_amount}}', '{{payment_3_date}}', '{{remaining_balance}}'].map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => {
                        if (templateEditor) {
                          templateEditor.chain().focus().insertContent(p).run();
                        }
                      }}
                      className="px-2 py-1 bg-[#F0EBE5] text-[#1B4D3E] rounded hover:bg-[#E5E0DB] transition-colors font-mono"
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-xs text-[#66605B] mt-2">Tip: Paste images directly with Ctrl+V</p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveTemplate} 
              disabled={saving}
              className="bg-[#1B4D3E] hover:bg-[#153E32]"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Template</DialogTitle>
          </DialogHeader>
          <p>Are you sure you want to delete "{templateToDelete?.name}"? This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteTemplate}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
